import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Shield } from "lucide-react";

const GRADE_COLORS = {
  "A+": "bg-emerald-500 text-white",
  "A":  "bg-emerald-500 text-white",
  "B":  "bg-lime-500 text-white",
  "C":  "bg-amber-500 text-white",
  "D":  "bg-orange-500 text-white",
  "F":  "bg-red-500 text-white",
  "—":  "bg-slate-200 text-slate-500",
};

export default function DeliverabilityBadge({ domainId }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tooltip, setTooltip] = useState(false);

  useEffect(() => {
    if (!domainId) return;
    api.get(`/domains/${domainId}/deliverability`, { params: { days: 30 } })
      .then((r) => setD(r.data))
      .catch(() => setD({ ok: false }))
      .finally(() => setLoading(false));
  }, [domainId]);

  if (loading || !d?.ok) return null;
  const color = GRADE_COLORS[d.grade] || GRADE_COLORS["—"];
  const unrated = d.grade === "—";

  return (
    <div
      className="relative inline-flex items-center"
      onMouseEnter={() => setTooltip(true)}
      onMouseLeave={() => setTooltip(false)}
      data-testid={`deliverability-${domainId}`}
    >
      <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${color}`}>
        <Shield className="h-2.5 w-2.5" />
        {unrated ? "Unrated" : `Grade ${d.grade}`}
      </span>
      {tooltip && (
        <div className="absolute top-full left-0 mt-2 z-20 w-64 rounded-lg bg-[#0F172A] text-white text-[11px] p-3 shadow-xl pointer-events-none">
          <div className="font-bold mb-1.5">
            {unrated ? "Not enough data yet" : `Deliverability: ${d.grade} (${d.score}/100)`}
          </div>
          {!unrated && (
            <div className="space-y-1 text-white/80">
              <Row label="Accepted" value={d.accepted} />
              <Row label="Bounce rate" value={`${d.bounce_rate}%`} good={d.bounce_rate < 2} bad={d.bounce_rate > 5} />
              <Row label="Complaint rate" value={`${d.complaint_rate}%`} good={d.complaint_rate < 0.1} bad={d.complaint_rate > 0.5} />
              <Row label="DKIM" value={d.dkim_valid ? "✓ valid" : "✗ invalid"} good={d.dkim_valid} bad={!d.dkim_valid} />
              <Row label="SPF" value={d.spf_valid ? "✓ valid" : "✗ invalid"} good={d.spf_valid} bad={!d.spf_valid} />
            </div>
          )}
          {unrated && (
            <div className="text-white/70">Send a few emails to this domain — we'll grade based on bounce rate, spam complaints, DKIM, and SPF.</div>
          )}
          <div className="text-white/40 text-[10px] mt-2 pt-2 border-t border-white/10">Last 30 days</div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, good, bad }) {
  const color = good ? "text-emerald-300" : bad ? "text-rose-300" : "text-white/80";
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-white/60">{label}</span>
      <span className={`font-mono font-semibold ${color}`}>{value}</span>
    </div>
  );
}
